import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Quaternion, Vector2, Vector4 } from "core/Maths/math.vector";
import { PropertyLine } from "./propertyLine";
import { Body1 } from "@fluentui/react-components";
import { Tools } from "core/Misc/tools";
import { CalculatePrecision } from "../../primitives/utils";
import { NumberInputPropertyLine } from "./inputPropertyLine";
import { TextPropertyLine } from "./textPropertyLine";
const HasZ = (vector) => !(vector instanceof Vector2);
const HasW = (vector) => vector instanceof Vector4 || vector instanceof Quaternion;
/**
 * Reusable component which renders a vector property line containing a label, vector value, and expandable XYZW values
 * The expanded section contains a slider/input box for each component of the vector (x, y, z, w)
 * @param props
 * @returns
 */
const TensorPropertyLine = (props) => {
    TensorPropertyLine.displayName = "TensorPropertyLine";
    const converted = (val) => (props.valueConverter ? props.valueConverter.from(val) : val);
    const formatted = (val) => converted(val).toFixed(props.step !== undefined ? Math.max(0, CalculatePrecision(props.step)) : 2);
    const [vector, setVector] = useState(props.value);
    const { min, max } = props;
    const onChange = (val, key) => {
        const value = props.valueConverter ? props.valueConverter.to(val) : val;
        const newVector = vector.clone();
        newVector[key] = value; // The syncedSlider for 'w' is only rendered when vector is a Vector4, so this is safe
        setVector(newVector);
        props.onChange(newVector);
    };
    useEffect(() => {
        setVector(props.value);
    }, [props.value, props.expandedContent]);
    return (_jsx(PropertyLine, { ...props, expandedContent: _jsxs(_Fragment, { children: [props.expandedContent, vector ? (_jsx(VectorSliders, { vector: vector, min: min, max: max, unit: props.unit, step: props.step, precision: props.precision, converted: converted, onChange: onChange })) : undefined] }), children: _jsx(Body1, { children: `[${formatted(props.value.x)}, ${formatted(props.value.y)}${HasZ(props.value) ? `, ${formatted(props.value.z)}` : ""}${HasW(props.value) ? `, ${formatted(props.value.w)}` : ""}]` }) }));
};
const VectorSliders = ({ vector, min, max, unit, step, precision, converted, onChange }) => (_jsxs(_Fragment, { children: [_jsx(NumberInputPropertyLine, { label: "X", value: converted(vector.x), min: min, max: max, onChange: (val) => onChange(val, "x"), unit: unit, step: step, precision: precision }), _jsx(NumberInputPropertyLine, { label: "Y", value: converted(vector.y), min: min, max: max, onChange: (val) => onChange(val, "y"), unit: unit, step: step, precision: precision }), HasZ(vector) && (_jsx(NumberInputPropertyLine, { label: "Z", value: converted(vector.z), min: min, max: max, onChange: (val) => onChange(val, "z"), unit: unit, step: step, precision: precision })), HasW(vector) && (_jsx(NumberInputPropertyLine, { label: "W", value: converted(vector.w), min: min, max: max, onChange: (val) => onChange(val, "w"), unit: unit, step: step, precision: precision }))] }));
const ToDegreesConverter = { from: Tools.ToDegrees, to: Tools.ToRadians };
export const RotationVectorPropertyLine = (props) => {
    RotationVectorPropertyLine.displayName = "RotationVectorPropertyLine";
    const step = props.useDegrees ? 1 : 0.01;
    const precision = props.useDegrees ? 1 : 2;
    return (_jsx(Vector3PropertyLine, { ...props, unit: props.useDegrees ? "°" : "rad", valueConverter: props.useDegrees ? ToDegreesConverter : undefined, step: step, precision: precision }));
};
const QuaternionPropertyLineInternal = TensorPropertyLine;
export const QuaternionPropertyLine = (props) => {
    QuaternionPropertyLine.displayName = "QuaternionPropertyLine";
    const step = props.useDegrees ? 1 : 0.01;
    const precision = props.useDegrees ? 1 : 2;
    const [quat, setQuat] = useState(props.value);
    useEffect(() => {
        setQuat(props.value);
    }, [props.value]);
    // Extract only the properties that exist on QuaternionPropertyLineProps
    const { useEuler, ...restProps } = props;
    const onQuatChange = (val) => {
        setQuat(val);
        props.onChange(val);
    };
    const onEulerChange = (val) => {
        const quat = Quaternion.FromEulerAngles(val.x, val.y, val.z);
        onQuatChange(quat);
    };
    return useEuler ? (_jsx(Vector3PropertyLine, { ...restProps, nullable: false, ignoreNullable: false, value: quat.toEulerAngles(), valueConverter: ToDegreesConverter, onChange: onEulerChange, unit: props.useDegrees ? "°" : "rad", step: step, precision: precision, expandedContent: _jsx(TextPropertyLine, { label: "Quaternion", value: `[${quat.x.toFixed(4)}, ${quat.y.toFixed(4)}, ${quat.z.toFixed(4)}, ${quat.w.toFixed(4)}]` }) })) : (_jsx(QuaternionPropertyLineInternal, { ...props, nullable: false, value: quat, onChange: onQuatChange, unit: props.useDegrees ? "°" : "rad", step: step, precision: precision }));
};
export const Vector2PropertyLine = TensorPropertyLine;
export const Vector3PropertyLine = TensorPropertyLine;
export const Vector4PropertyLine = TensorPropertyLine;
//# sourceMappingURL=vectorPropertyLine.js.map